import { NgOptimizedImage } from '@angular/common';
import { Component, HostBinding, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { CrossSellConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { LiveCasinoCategoryModel, LiveCasinoGame } from '@frontend/sports/types/components/live-casino';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService, WINDOW } from '@frontend/vanilla/core';
import { KycStatus, KycStatusService } from '@frontend/vanilla/shared/kyc';

import { GamesService } from '../banner/games.service';
import { LaunchSource } from '../cross-sell-data/cross-sell-data.model';
import { CrossSellDataService } from '../cross-sell-data/cross-sell-data.service';
import { CrossSellTrackingService } from '../cross-sell-data/cross-sell-tracking.service';
import { LiveCasinoGamesModel } from './live-event-list.service';

@HooksWireup()
@Component({
    selector: 'ms-live-casino',
    templateUrl: './live-casino.component.html',
    styleUrl: './live-casino.component.scss',
    imports: [ScrollAdapterComponent, NgOptimizedImage],
})
export class LiveCasinoComponent extends OnRouteResolve<LiveCasinoGamesModel | undefined> {
    @HostBinding('class') className = 'casino-games-var-scope';

    categories: LiveCasinoCategoryModel[];

    private crossSellDataService = inject(CrossSellDataService);
    private window = inject(WINDOW);
    private userService = inject(UserService);
    private redirectService = inject(RedirectHelperService);
    private gamesService = inject(GamesService);
    private deviceService = inject(DeviceService);
    private crossSellTrackingService = inject(CrossSellTrackingService);
    private crossSellConfig = inject(CrossSellConfig);
    private kycStatusService = inject(KycStatusService);

    constructor(route: ActivatedRoute) {
        super(route);
        this.userService.onUserLogin$.pipe(takeUntilDestroyed()).subscribe(() => this.getLiveCasinoGamesByKycStatus());
    }

    onRouteResolved(model: LiveCasinoGamesModel | undefined): void {
        if (!model) {
            return;
        }

        this.categories = model.liveCasinoGames;
        this.crossSellTrackingService.trackGameImpressions(this.categories);
    }

    private getLiveCasinoGamesByKycStatus(): void {
        if (this.crossSellConfig.isKYCCheckEnabled) {
            this.kycStatusService.kycStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status: KycStatus | null) => {
                this.crossSellDataService.getCasinoGames(status?.kycVerified).subscribe((result) => {
                    this.categories = result;
                    this.crossSellTrackingService.trackGameImpressions(this.categories);
                });
            });
        }
    }

    launchGame(category: LiveCasinoCategoryModel, game: LiveCasinoGame, categoryPosition: number, gamePosition: number): void {
        if (!this.userService.isAuthenticated) {
            this.redirectService.goToLogin({ appendReferrer: true });

            return;
        }

        this.crossSellTrackingService.trackLiveCasinoGameLaunch({
            gameName: game.id,
            gameType: game.provider,
            gamePosition: gamePosition,
            gridPosition: `${categoryPosition}:${gamePosition}`,
            gameDisplayName: game.title,
            gameCategoryType: category.name,
            gameSubCategoryType: category.subCategoryName,
        });

        const gameLaunchUrl = this.gamesService.getLaunchUrl(game.id, LaunchSource.LiveCasino);

        if (this.deviceService.isMobile) {
            this.window.open(gameLaunchUrl, '_self');
        } else {
            this.window.open(gameLaunchUrl, 'blank', 'width=800, height=800');
        }
    }

    getGameDataProductId(category: LiveCasinoCategoryModel, game: LiveCasinoGame): string {
        return `${category.id}${category.subCategoryName}_${game.id}`;
    }
}
